import * as THREE from 'three';
import { scene, camera, active } from './scene.js';
import { spawnSparks, spawnBoom } from './fx.js';
import { noise, tone, boomOut, playFx, playFxAt, startAmbient, setTinnitus } from './audio.js';
import { hurtPlayer } from './player.js';
import { onMessage, OP, me, sendThrow, NADE_KIND, NADE_NAME, unpackPos, unpackUnit } from './net.js';
import { makeNade } from './nademodel.js';
import {
  player, S, clock, wallMeshes, particles,
  grenades, smokes, fires, R, aimDir, _wp, _wd,
} from './state.js';
import { nearBox, nearPoint } from './mapgrid.js';

// ============================================================
//  Гранаты: осколочная / дымовая / светошумовая / молотов.
//  Берутся в руку (4/5/6/7), замах зажатием ЛКМ, бросок отпусканием.
//
//  ⚠️ ГЛАВНОЕ ПРО АРХИТЕКТУРУ (изменено при выводе гранат на сервер):
//  в онлайне физику полёта, точку взрыва, урон и силу ослепления считает
//  ТОЛЬКО сервер (shared/src/grenade.rs). Клиент здесь:
//    • шлёт намерение броска (тип, углы, замах) — sendThrow();
//    • рисует СВОЮ гранату предсказанно, чтобы она вылетала из руки сразу,
//      а не через полпинга, и подменяет её серверной копией по OP_NADE;
//    • рисует чужие гранаты по OP_NADE и все взрывы по OP_BOOM;
//    • НЕ наносит урона: hurtPlayer работает только офлайн. Урон по NPC
//      тоже целиком серверный — боты переехали туда же (server/src/npc.rs).
//
//  ⚠️ Физика ниже (bounceAABB + шаг в stepNade) — построчный порт
//  shared/src/grenade.rs. **Менять только вместе с ним**, иначе линия превью
//  покажет одно, а сервер взорвёт в другом месте.
// ============================================================

/** Шаг симуляции гранаты — ровно серверный тик, иначе траектории разъедутся */
const NADE_DT = 1 / 30;

/**
 * Высота «дна мира» — зеркало grenade.rs::FLOOR_Y.
 * ⚠️ Это НЕ уровень пола карты: на импортированной de_dust2 пол собран из
 * боксов и лежит на 0.8, террасы уходят до 7.6. Здесь только страховка от
 * падения в бесконечность там, где боксов нет вовсе.
 */
const FLOOR_Y = 0.1;

/**
 * Максимальное перемещение гранаты за один подшаг физики, м —
 * зеркало `grenade.rs::MAX_SUBSTEP`. **Менять только парой.**
 *
 * ⚠️ Баг-урок «гранаты проваливаются под землю на новой карте»: отскок
 * проверяет ТОЧКУ внутри ящика, а не отрезок пути. Карта вокселизована шагом
 * 0.4 м, пол — плиты ровно такой толщины, а граната летит 36 м/с, то есть
 * 1.2 м за тик. Перед шагом она над плитой, после — уже под ней, и внутри её
 * не застаёт ни одна проверка. На старой арене пол был клампом `FLOOR_Y`,
 * сквозь который пролететь нельзя, — поэтому баг и вылез только с импортом.
 */
const MAX_SUBSTEP = 0.15;

/** Предел подшагов на тик — зеркало `grenade.rs::MAX_SUBSTEPS` */
const MAX_SUBSTEPS = 24;

/**
 * Сколько горит лужа молотова, с. ⚠️ Зеркало `grenade.rs::FIRE_LIFE`: по нему
 * сервер считает тик-урон, а клиент — и картинку, и звук горения. Разъедутся
 * — огонь будет гореть беззвучно или шуметь на пустом месте.
 */
const FIRE_LIFE = 18;

/**
 * Предел разлива лужи от точки удара, м — зеркало `grenade.rs::FIRE_RADIUS`.
 *
 * ⚠️ С 2026-08-06 это ПРЕДЕЛ, а не форма: лужа больше не круг (см. `spill`).
 */
const FIRE_RADIUS = 9;

// ---- Разлив лужи по полу — зеркало `grenade.rs::spill`. Менять только парой ----
//
// ⚠️ Зачем переделано (жалоба владельца): круглая лужа радиусом 9 м не знала
// ни про стены, ни про рельеф под собой. Отсюда «поджигает то, что за стеной
// и за укрытием» и «висит в воздухе» — язычки рисовались на высоте ЦЕНТРА
// лужи по всему кругу, в том числе над обрывом и по ту сторону простенка.
// Теперь лужа — набор клеток пола, залитых от точки удара: у каждой клетки
// своя высота, а сквозь стены горючее не течёт.
/** Шаг сетки лужи, м — `grenade.rs::FIRE_CELL`. */
const FIRE_CELL = 1.5;
/** Радиус пятна вокруг центра клетки, м — `grenade.rs::FIRE_CELL_R`
 *  (полдиагонали клетки с запасом, иначе между клетками холодные щели). */
const FIRE_CELL_R = 1.1;
/** На сколько огонь всходит по ступеньке, м — `grenade.rs::FIRE_RISE`. */
const FIRE_RISE = 0.5;
/** И на сколько стекает вниз, м — `grenade.rs::FIRE_FALL`. */
const FIRE_FALL = 2.5;
/** Высота поиска преграды между клетками, м — `grenade.rs::FIRE_PROBE`. */
const FIRE_PROBE = 0.3;

/**
 * Сколько секунд человек догорает, выйдя из лужи, с —
 * зеркало `grenade.rs::BURN_AFTER`. **Менять только парой.**
 *
 * Онлайн урон догорания считает сервер (клиент узнаёт о нём только флагом
 * `F_BURNING` и кадрами `OP_DAMAGE`), офлайн — код ниже сам.
 *
 * ⚠️ Сокращено с 5 до 2 вместе с сервером (решение владельца): пять секунд
 * догорания обесценивали выход из лужи — урон догонял всё равно.
 */
const BURN_AFTER = 2;

/** Между тиками урона огня, с — зеркало `grenade.rs::FIRE_TICK`. */
const FIRE_TICK = 0.4;

/**
 * Урон за тик горения по СЕБЕ (офлайн-песочница) — зеркало
 * `grenade.rs::FIRE_SELF_DMG`. ⚠️ Уменьшен вдвое вместе с сервером.
 * Онлайн это число не используется вовсе: там урон приходит кадрами.
 */
const FIRE_SELF_DMG = 4;

/**
 * Грани удара — зеркало `grenade.rs::Face`. Приезжают байтом в кадре `OP_BOOM`.
 * ⚠️ Номера менять нельзя: это протокол.
 */
const FACE = { FLOOR: 0, CEIL: 1, XP: 2, XN: 3, ZP: 4, ZN: 5, BODY: 6 };
/** «Поверхности нет» — у всех гранат, кроме молотова (`grenade::FACE_NONE`). */
const FACE_NONE = 255;
/** Вертикальная ли грань: по стене огонь надо разливать, по полу — нет. */
const isWallFace = (f) => f === FACE.XP || f === FACE.XN || f === FACE.ZP || f === FACE.ZN;
/** Нормаль грани наружу — зеркало `grenade.rs::Face::normal` */
const FACE_NORMAL = {
  [FACE.FLOOR]: [0, 1, 0], [FACE.CEIL]: [0, -1, 0],
  [FACE.XP]: [1, 0, 0], [FACE.XN]: [-1, 0, 0],
  [FACE.ZP]: [0, 0, 1], [FACE.ZN]: [0, 0, -1],
  [FACE.BODY]: [0, 1, 0],
};

/**
 * На сколько метров по вертикали лужа достаёт от своего уровня —
 * зеркало `grenade.rs::FIRE_HEIGHT`. **Менять только парой.**
 * Нужно с рельефной картой: без него молотов со двора жжёт стоящего на
 * террасе семью метрами выше.
 */
const FIRE_HEIGHT = 2.0;

/**
 * Сколько дымовая травит газ, с. Отдельное число от жизни облака (30 с):
 * шипит выпуск, а не сам дым — висящее облако беззвучно. Серверного
 * аналога нет: дыма на сервере не существует вовсе (см. CLAUDE.md).
 */
const SMOKE_HISS = 12;

/**
 * Опорная дистанция затухания взрыва, м (см. `REF_DIST` в audio.js).
 * Большая: взрыв слышно почти через всю карту, но направление и удалённость
 * по нему читаются. Это параметр ОЩУЩЕНИЯ, серверных радиусов здесь нет —
 * `OP_BOOM` приходит всем без отбора по дальности.
 */
const BOOM_REF = 34;

export const THROW_SPEED = 36, THROW_UP = 4;   // в 2 раза дальше прежнего (было 18)
export const THROW_MIN = 15;                   // слабый бросок (только начал замах)

// Граната «в руке»: GLB-модель + рука, держащая её + линия траектории броска.
// Модель живёт в holder'е (позиция и наклон хвата), а сам замах двигает
// nadeHand (updateNadeAim) — как раньше двигал шарик. Модель подставляется
// под тип в equipGrenade через makeNade (см. nademodel.js).
const nadeHand = new THREE.Group(); camera.add(nadeHand); nadeHand.visible = false;
const nadeHandHolder = new THREE.Group();
nadeHandHolder.position.set(0.20, -0.22, -0.42);
nadeHandHolder.rotation.set(0.25, 0.4, 0.1);   // лёгкий наклон — читается как в кулаке
nadeHandHolder.scale.setScalar(1.3);           // крупнее, чтобы торчала над кулаком
nadeHand.add(nadeHandHolder);

// Рука вьюмодели: предплечье-цилиндр из нижнего угла кадра + кисть-сфера на
// гранате. Без неё граната «висела в воздухе» (жалоба владельца) — арм
// прикрепляет её к телу визуально.
// ⚠️ Копия makeViewArm из weapons.js, а НЕ импорт: weapons.js уже импортирует
// exitNade отсюда, и обратный импорт замкнул бы цикл weapons ↔ grenades (класс
// TDZ-ошибок, что ловит только headless-smoke). Рука одна (правая) — короче
// оригинала, где их две под хват ствола.
// ⚠️ Кожа кисти ПРОЗРАЧНАЯ (решение владельца) — см. блок ниже. `depthWrite:false`
// обязателен: без него ладонь, попав в прозрачный проход раньше пальцев, срезала
// бы их и гранату по буферу глубины — сквозь неё была бы видна не граната, а фон.
const nadeArmSkin = new THREE.MeshStandardMaterial({
  color: 0xba8b66, roughness: 0.85, transparent: true, opacity: 0.34, depthWrite: false,
});
// Пальцы плотнее ладони: полосы поперёк шара — то, из-за чего он читается кистью.
const nadeFingerSkin = new THREE.MeshStandardMaterial({
  color: 0xc2926c, roughness: 0.85, transparent: true, opacity: 0.52, depthWrite: false,
});
const nadeArmSleeve = new THREE.MeshStandardMaterial({ color: 0x39423a, roughness: 0.9, metalness: 0.05 });
const NADE_SHOULDER = new THREE.Vector3(0.30, -0.55, 0.30);   // нижний правый угол кадра
{
  // ⚠️ Кисть обхватывает гранату ЦЕЛИКОМ, но прозрачна. История решения:
  //  1) кулак-сфера стоял по центру и накрывал гранату — «не видно, какую держим»;
  //  2) кисть опустили ниже центра — тип стало видно, но хват читался как
  //     «граната лежит на кулаке», а не в нём;
  //  3) сейчас — полный обхват + прозрачная кожа: хват честный, а силуэт и
  //     цветная полоса типа (см. md/graphics.md) просвечивают сквозь ладонь.
  // Рукав предплечья при этом НЕ прозрачный — иначе рука перестаёт крепить
  // гранату к телу, ради чего её и добавляли.
  const grip = nadeHandHolder.position.clone(); grip.y -= 0.02;
  const dir = new THREE.Vector3().subVectors(grip, NADE_SHOULDER);
  const dirN = dir.clone().normalize();
  // Не доводим рукав до центра хвата — последние ~5 см занимает сам кулак.
  const len = Math.max(0.05, dir.length() - 0.05);
  const forearm = new THREE.Mesh(new THREE.CylinderGeometry(0.038, 0.055, len, 10), nadeArmSleeve);
  forearm.position.copy(NADE_SHOULDER).addScaledVector(dirN, len / 2);
  forearm.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dirN);
  forearm.frustumCulled = false;
  nadeHand.add(forearm);

  // Кулак повёрнут так же, как holder: тогда пальцы ложатся ПОПЕРЁК оси гранаты,
  // а не наискось от неё.
  const fist = new THREE.Group();
  fist.position.copy(grip);
  fist.rotation.copy(nadeHandHolder.rotation);
  fist.frustumCulled = false;
  nadeHand.add(fist);

  const palm = new THREE.Mesh(new THREE.SphereGeometry(0.062, 12, 10), nadeArmSkin);
  palm.scale.set(1.1, 0.92, 1.15); palm.frustumCulled = false;
  fist.add(palm);

  // Четыре пальца поперёк гранаты: средние вынесены к камере чуть дальше крайних —
  // так ряд огибает тело гранаты, а не лежит плоской решёткой.
  const fingerGeo = new THREE.CylinderGeometry(0.0125, 0.0125, 0.10, 8);
  for (let i = 0; i < 4; i++) {
    const f = new THREE.Mesh(fingerGeo, nadeFingerSkin);
    f.rotation.z = Math.PI / 2;    // цилиндр вдоль X — поперёк оси гранаты
    f.position.set(0, 0.034 - i * 0.022, 0.058 - Math.abs(i - 1.5) * 0.008);
    f.frustumCulled = false;
    fist.add(f);
  }
  // Большой палец — вдоль гранаты с внутренней стороны кулака (ближе к центру кадра).
  const thumb = new THREE.Mesh(new THREE.CylinderGeometry(0.0145, 0.0145, 0.075, 8), nadeFingerSkin);
  thumb.rotation.set(0, 0, -0.32);
  thumb.position.set(-0.046, -0.004, 0.030);
  thumb.frustumCulled = false;
  fist.add(thumb);
}

const nadeArc = new THREE.Line(new THREE.BufferGeometry(),
  new THREE.LineBasicMaterial({ color: 0xffe066, transparent: true, opacity: 0.85 }));
nadeArc.visible = false; nadeArc.frustumCulled = false; scene.add(nadeArc);

const landMark = new THREE.Mesh(new THREE.RingGeometry(0.25, 0.38, 20),
  new THREE.MeshBasicMaterial({ color: 0xffe066, transparent: true, opacity: 0.8, side: THREE.DoubleSide, depthWrite: false }));
landMark.rotation.x = -Math.PI / 2; landMark.visible = false; scene.add(landMark);

function puffTexture() {
  const s = 128, c = document.createElement('canvas'); c.width = c.height = s;
  const g = c.getContext('2d');
  // Плотное облачко: почти непрозрачное ядро на 60 % радиуса и мягкий спад до
  // нуля только у самой кромки (резкой границы быть не должно — иначе клуб
  // читается «квадратом»).
  //
  // ⚠️ Раньше спад начинался от центра (0.85 → 0.4 на половине радиуса): из
  // сотни таких клубов складывалась дымка, сквозь которую было видно, а не
  // стена. Плотность дыма делает именно ядро текстуры, а не количество
  // спрайтов, — и это дешевле для GPU (см. баг-урок про fill rate).
  const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.6, 'rgba(255,255,255,0.92)');
  grd.addColorStop(0.82, 'rgba(255,255,255,0.5)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd; g.fillRect(0, 0, s, s); return new THREE.CanvasTexture(c);
}
const PUFF = puffTexture();

const losRay = new THREE.Raycaster();

// Взять гранату в руку (показать траекторию). Зажать ЛКМ — замах, отпустить — бросок.
export function equipGrenade(type) {
  if (!active() || !player.alive) return;
  // Уже держим этот тип: перехватом гранаты отменяем замах (если он копится),
  // но не переигрываем заново взятие в руку.
  if (S.nadeMode === type) { S.nadeCharge = 0; S.semiQueued = false; S.firing = false; return; }
  S.nadeMode = type; S.nadeCharge = 0; S.semiQueued = false; S.firing = false;
  // Сменить модель в руке под тип. frustumCulled off: вьюмодель у самой камеры,
  // при наклоне её bounding sphere уезжает за край кадра и Three её срезал бы.
  nadeHandHolder.clear();
  const held = makeNade(type).group;
  held.traverse((o) => { if (o.isMesh) o.frustumCulled = false; });
  nadeHandHolder.add(held);
  nadeHand.visible = true; nadeArc.visible = true; landMark.visible = true;
  tone(360, 0.04, 0.1, 'square');
}

export function exitNade() {
  S.nadeMode = null; S.nadeCharge = 0;
  // ⚠️ Гасим и намерение стрельбы. Замах отменяют, НЕ отпуская ЛКМ, — а зажатая
  // кнопка тут же выстрелила бы из только что взятого ствола (авто-оружие стреляет
  // по S.firing). Тот же капкан, что с semiQueued: флаг переживает состояние,
  // в котором его чистят. После отмены нужен новый клик.
  S.firing = false; S.semiQueued = false;
  nadeHand.visible = false; nadeArc.visible = false; landMark.visible = false;
  nadeHand.position.set(0, 0, 0); nadeHand.rotation.set(0, 0, 0);
}

/** Онлайн ли мы — офлайн вся старая клиентская логика урона остаётся рабочей */
const online = () => S.netState === 'online';

// Вектор скорости броска: сила зависит от замаха (charge 0..1).
// ⚠️ Направление берём из S.yaw/S.pitch (как сервер), а НЕ из камеры:
// у камеры есть покачивание, наклон и тряска, которых на сервере нет.
function throwVelocity(charge) {
  aimDir(S.yaw, Math.max(-1.6, Math.min(1.6, S.pitch)), _wd);
  const sp = THROW_MIN + (THROW_SPEED - THROW_MIN) * charge;
  return _wd.clone().multiplyScalar(sp).add(new THREE.Vector3(0, THROW_UP * (0.6 + 0.4 * charge), 0));
}

/** Точка рождения гранаты — зеркало grenade.rs::throw_origin */
function throwOrigin() {
  aimDir(S.yaw, Math.max(-1.6, Math.min(1.6, S.pitch)), _wd);
  return player.pos.clone().addScaledVector(_wd, 0.5);
}

// Отскок точки от AABB-ящика: выталкивание по оси наименьшего проникновения.
// Общая для реальной гранаты и для превью-траектории — иначе линия врала бы.
//
// ⚠️ Баг-урок «граната исчезает в углу ящика у земли»: у ящика, стоящего на
// полу, низ НЕ является выходом. Вытолкнув туда гранату, мы загоняем её под
// пол, откуда её выпихивает кламп FLOOR_Y, и она уезжает сквозь ящик насквозь.
//
// ⚠️ Продолжение того же урока (новая карта). Раньше «низ закрыт» решалось
// сравнением `c.min.y < FLOOR_Y`, то есть «ящик стоит на полу мира». На
// импортированной de_dust2 пол сам сделан из боксов и лежит на 0.8 — значит
// НИ ОДИН ящик карты ту проверку не проходил, и защита была выключена целиком.
// Критерий должен быть не про абсолютную высоту, а про то, откуда граната
// пришла: снизу в перекрытие входят только те, кто летит ВВЕРХ.
//
// ⚠️ Экспортируется и используется ещё и трупами (`corpse.js`) — намеренно,
// вместо копии. Копия развалила бы ровно то, ради чего этот код и написан:
// выбор оси выталкивания. Коэффициенты отскока трупу не подходят (тело не
// мяч), поэтому он гасит скорость сам, ПОСЛЕ вызова — а геометрия остаётся
// одна на всех и по-прежнему зеркалит `grenade.rs::bounce_aabb`.
export function bounceAABB(p, v, gr) {
  let landed = false;
  // ⚠️ Кандидаты — из сетки (mapgrid.js), в порядке номеров боксов, как на
  // сервере: выталкивание накопительное, и порядок меняет итог. Радиус с
  // запасом (gr + 1.5) — точка переставляется на грань бокса прямо в цикле,
  // и следующий бокс проверяется уже по новой позиции.
  //
  // ⚠️ Это горячий код: `updateNadeAim` прогоняет 200 шагов траектории КАЖДЫЙ
  // кадр, пока граната в руке. Перебором всей карты это было 350 000 проверок
  // на кадр — линия броска в одиночку съедала кадровый бюджет.
  nearPoint(p.x, p.z, gr + 1.5, (c) => {
    if (p.x > c.min.x - gr && p.x < c.max.x + gr && p.y > c.min.y - gr && p.y < c.max.y + gr
      && p.z > c.min.z - gr && p.z < c.max.z + gr) {
      const px = Math.min(p.x - (c.min.x - gr), (c.max.x + gr) - p.x);
      const pz = Math.min(p.z - (c.min.z - gr), (c.max.z + gr) - p.z);
      // Низ — выход только для летящего ВВЕРХ (то есть ударившего снизу).
      // Для падающей и катящейся гранаты низ — не грань, а «под землю».
      // Второе условие — прежняя защита дна мира: под ящик, стоящий на самом
      // полу, не выталкиваем даже летящую вверх.
      const pyDown = (v.y > 0 && c.min.y - gr >= FLOOR_Y) ? p.y - (c.min.y - gr) : Infinity;
      const pyUp = (c.max.y + gr) - p.y;
      const py = Math.min(pyDown, pyUp);
      const mn = Math.min(px, py, pz);
      if (mn === px) { p.x = p.x < (c.min.x + c.max.x) / 2 ? c.min.x - gr : c.max.x + gr; v.x *= -0.5; }
      else if (mn === pz) { p.z = p.z < (c.min.z + c.max.z) / 2 ? c.min.z - gr : c.max.z + gr; v.z *= -0.5; }
      else if (pyDown < pyUp) { p.y = c.min.y - gr; v.y = Math.min(v.y, 0); } // удар снизу в перекрытие
      else { p.y = c.max.y + gr; v.y *= -0.35; v.x *= 0.7; v.z *= 0.7; landed = true; } // легла на верх
    }
  });
  return landed;
}

/**
 * Контакт БЕЗ отскока — порт `grenade.rs::shatter_face`. Точка ставится на
 * поверхность, возвращается её грань (`FACE.*`) либо −1, если касания нет.
 *
 * Это молотовская половина `bounceAABB`: геометрия выбора грани та же, но
 * скорость не отражается. Бутылка не мяч — она бьётся о первое, чего
 * коснулась. **Менять только вместе с сервером.**
 */
function shatterFace(p, v, gr) {
  let face = -1;
  nearPoint(p.x, p.z, gr + 1.5, (c) => {
    if (face >= 0) return;                       // уже разбилась — дальше не смотрим
    if (p.x > c.min.x - gr && p.x < c.max.x + gr && p.y > c.min.y - gr && p.y < c.max.y + gr
      && p.z > c.min.z - gr && p.z < c.max.z + gr) {
      const px = Math.min(p.x - (c.min.x - gr), (c.max.x + gr) - p.x);
      const pz = Math.min(p.z - (c.min.z - gr), (c.max.z + gr) - p.z);
      const pyDown = (v.y > 0 && c.min.y - gr >= FLOOR_Y) ? p.y - (c.min.y - gr) : Infinity;
      const pyUp = (c.max.y + gr) - p.y;
      const py = Math.min(pyDown, pyUp);
      const mn = Math.min(px, py, pz);
      if (mn === px) {
        if (p.x < (c.min.x + c.max.x) / 2) { p.x = c.min.x - gr; face = FACE.XN; }
        else { p.x = c.max.x + gr; face = FACE.XP; }
      } else if (mn === pz) {
        if (p.z < (c.min.z + c.max.z) / 2) { p.z = c.min.z - gr; face = FACE.ZN; }
        else { p.z = c.max.z + gr; face = FACE.ZP; }
      } else if (pyDown < pyUp) { p.y = c.min.y - gr; face = FACE.CEIL; }
      else { p.y = c.max.y + gr; face = FACE.FLOOR; }
    }
  });
  return face;
}

/**
 * Грань, о которую разбилась бутылка на последнем шаге (−1 — не разбилась).
 *
 * Модульная переменная, а не возвращаемое значение: `stepNade` зовётся 200 раз
 * за кадр для линии превью, и создавать там объект-результат — мусор для GC на
 * ровном месте. Читать сразу после вызова.
 */
let lastFace = -1;

/**
 * Перемещение с отскоками — порт `grenade.rs::advance`. Гравитации и фитиля
 * здесь НЕТ: они считаются раз в тик, а это тело крутится подшагами.
 *
 * `fragile` — молотов: он не отскакивает НИ ОТ ЧЕГО, первое касание его бьёт.
 */
function advanceNade(pos, vel, dt, fragile) {
  const lim = R - 0.5;
  pos.addScaledVector(vel, dt);
  let landed = false;
  if (pos.y < FLOOR_Y) {
    pos.y = FLOOR_Y;
    if (fragile) return shatterBottle(vel, FACE.FLOOR);
    vel.y *= -0.35; vel.x *= 0.6; vel.z *= 0.6; landed = true;
  }
  if (pos.x < -lim) { pos.x = -lim; if (fragile) return shatterBottle(vel, FACE.XP); vel.x *= -0.5; }
  if (pos.x > lim) { pos.x = lim; if (fragile) return shatterBottle(vel, FACE.XN); vel.x *= -0.5; }
  if (pos.z < -lim) { pos.z = -lim; if (fragile) return shatterBottle(vel, FACE.ZP); vel.z *= -0.5; }
  if (pos.z > lim) { pos.z = lim; if (fragile) return shatterBottle(vel, FACE.ZN); vel.z *= -0.5; }
  if (fragile) {
    const f = shatterFace(pos, vel, 0.12);
    if (f >= 0) return shatterBottle(vel, f);
    return false;
  }
  if (bounceAABB(pos, vel, 0.12)) landed = true;
  return landed;
}

/** Бутылка разбилась: движение прекращается, грань запоминается. */
function shatterBottle(vel, face) {
  vel.set(0, 0, 0);
  lastFace = face;
  return true;
}

/**
 * Один шаг полёта — порт shared/src/grenade.rs::step.
 *
 * Гравитация применяется РАЗ за тик (иначе скорость менялась бы внутри тика,
 * и делить шаг пришлось бы ещё и серверу по-другому), а перемещение идёт
 * подшагами не длиннее `MAX_SUBSTEP` — иначе граната прошивает пол карты.
 *
 * `fragile` — молотов. Для него возвращённое `true` означает не «легла»,
 * а «РАЗБИЛАСЬ»; грань удара лежит в `lastFace`.
 */
function stepNade(pos, vel, dt, fragile = false) {
  lastFace = -1;
  vel.y -= 20 * dt;
  const sub = Math.min(MAX_SUBSTEPS, Math.max(1, Math.ceil(vel.length() * dt / MAX_SUBSTEP)));
  const sdt = dt / sub;
  let landed = false;
  for (let i = 0; i < sub; i++) {
    if (advanceNade(pos, vel, sdt, fragile)) landed = true;
    if (fragile && lastFace >= 0) break;   // разбитая бутылка дальше не летит
  }
  return landed;
}

/**
 * Уровень пола ПОД точкой — порт `grenade.rs::floor_under`. **Менять парой.**
 *
 * Нужен молотову: о стену бутылка бьётся на высоте, а гореть лужа обязана
 * внизу, у её подножия. Допуск 0.2 сверху — точка удара стоит на радиус
 * гранаты выше самой грани.
 */
function floorUnder(p) {
  let y = FLOOR_Y;
  nearPoint(p.x, p.z, 0.05, (c) => {
    if (p.x > c.min.x && p.x < c.max.x && p.z > c.min.z && p.z < c.max.z
      && c.max.y <= p.y + 0.2 && c.max.y > y) y = c.max.y;
  });
  return y;
}

/**
 * Верх самого высокого бокса под точкой, но не выше `ceil` —
 * порт `grenade.rs::floor_below`. Считается по УЖЕ отобранному списку.
 */
function floorBelow(boxes, x, z, ceil) {
  let y = FLOOR_Y;
  for (const c of boxes) {
    if (x > c.min.x && x < c.max.x && z > c.min.z && z < c.max.z
      && c.max.y <= ceil && c.max.y > y) y = c.max.y;
  }
  return y;
}

/** Пересекает ли отрезок хоть один бокс (метод плит) — порт `grenade.rs::seg_blocked`. */
function segBlocked(ax, ay, az, bx, by, bz, boxes) {
  const d = [bx - ax, by - ay, bz - az];
  const a = [ax, ay, az];
  for (const c of boxes) {
    const mn = [c.min.x, c.min.y, c.min.z], mx = [c.max.x, c.max.y, c.max.z];
    let t0 = 0, t1 = 1, hit = true;
    for (let k = 0; k < 3; k++) {
      if (Math.abs(d[k]) < 1e-6) {
        if (a[k] < mn[k] || a[k] > mx[k]) { hit = false; break; }
      } else {
        let ta = (mn[k] - a[k]) / d[k], tb = (mx[k] - a[k]) / d[k];
        if (ta > tb) { const s = ta; ta = tb; tb = s; }
        t0 = Math.max(t0, ta); t1 = Math.min(t1, tb);
        if (t0 > t1) { hit = false; break; }
      }
    }
    if (hit) return true;
  }
  return false;
}

/**
 * Разлить лужу от точки удара — порт `grenade.rs::spill`. **Менять парой.**
 *
 * Возвращает массив клеток `{x, y, z}`; первая — точка удара, снесённая
 * на пол под ней. Заливка идёт по четырём соседям (диагоналей нет: шаг
 * по диагонали срезал бы угол здания и просочился сквозь стену).
 */
function spill(hit) {
  const ground = floorUnder(hit);
  const ox = hit.x, oz = hit.z;
  // Боксы вокруг лужи отбираем ОДИН раз: перебирать всю карту на каждую
  // клетку и каждый отрезок — это миллион проверок на одну бутылку.
  const boxes = [];
  const pad = FIRE_RADIUS + FIRE_CELL;
  nearBox(ox - pad, oz - pad, ox + pad, oz + pad, (c) => {
    if (c.max.y >= ground - 12 && c.min.y <= ground + 6) boxes.push(c);
  });

  const n = Math.ceil(FIRE_RADIUS / FIRE_CELL);
  const side = n * 2 + 1;
  const seen = new Uint8Array(side * side);
  const at = (i, j) => (j + n) * side + (i + n);
  const cells = [{ x: ox, y: ground, z: oz }];
  const queue = [[0, 0, ground]];
  seen[at(0, 0)] = 1;
  for (let head = 0; head < queue.length; head++) {
    const [i, j, y] = queue[head];
    for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const ni = i + di, nj = j + dj;
      if (Math.abs(ni) > n || Math.abs(nj) > n || seen[at(ni, nj)]) continue;
      const nx = ox + ni * FIRE_CELL, nz = oz + nj * FIRE_CELL;
      if (Math.hypot(nx - ox, nz - oz) > FIRE_RADIUS) continue;
      const ny = floorBelow(boxes, nx, nz, y + FIRE_RISE);
      if (ny < y - FIRE_FALL) continue;
      const h = Math.max(y, ny) + FIRE_PROBE;
      if (segBlocked(ox + i * FIRE_CELL, h, oz + j * FIRE_CELL, nx, h, nz, boxes)) continue;
      seen[at(ni, nj)] = 1;
      cells.push({ x: nx, y: ny, z: nz });
      queue.push([ni, nj, ny]);
    }
  }
  return cells;
}

/** Горит ли под ногами `feet` — порт `grenade::FirePatch::covers`. */
function fireCovers(cells, fx, fy, fz) {
  for (const c of cells) {
    const dx = fx - c.x, dz = fz - c.z;
    if (dx * dx + dz * dz < FIRE_CELL_R * FIRE_CELL_R && Math.abs(fy - c.y) < FIRE_HEIGHT) return true;
  }
  return false;
}

// Пересчёт линии траектории (та же физика, что в updateGrenades) + анимация замаха руки
const _arcPts = [];
export function updateNadeAim() {
  // рука оттягивается назад/вверх пропорционально силе замаха
  nadeHand.position.set(S.nadeCharge * 0.06, S.nadeCharge * 0.05, S.nadeCharge * 0.14);
  nadeHand.rotation.x = -S.nadeCharge * 0.8;
  // Стартовая точка и шаг — ровно те же, что у сервера, иначе линия врёт
  const v = throwVelocity(S.nadeCharge); const p = throwOrigin();
  const fragile = S.nadeMode === 'molotov';
  _arcPts.length = 0;
  // полная симуляция с отскоками — чтобы линия показывала рикошет, а не «прилипала» к стене
  for (let i = 0; i < 200; i++) {
    _arcPts.push(p.clone());
    const landed = stepNade(p, v, NADE_DT, fragile);
    // Молотов линию обрывает СРАЗУ: он не отскакивает, а бьётся о первое
    // касание, и рисовать ему рикошет значило бы обещать то, чего не будет.
    if (fragile && landed) { _arcPts.push(p.clone()); break; }
    // Остановка, когда граната фактически улеглась на опоре.
    // ⚠️ Раньше условием была высота (`p.y <= 0.11`), то есть дно мира. На
    // импортированной карте пол лежит на 0.8, а террасы — до 7.6, и линия
    // никогда не «доходила»: превью крутило все 200 шагов, а кольцо-маркер
    // вставало не туда, где граната легла. Признак покоя — касание опоры
    // и погасшая скорость, высота тут ни при чём.
    if (landed && Math.hypot(v.x, v.z) < 0.4 && Math.abs(v.y) < 0.8) { _arcPts.push(p.clone()); break; }
  }
  nadeArc.geometry.setFromPoints(_arcPts);
  const last = _arcPts[_arcPts.length - 1];
  landMark.position.set(last.x, last.y + 0.03, last.z);
}

function nadeMesh(type) {
  const { group } = makeNade(type);   // летящая: общий шаблон, без own/ghost
  scene.add(group); return group;
}

// Снять летящую гранату. Геометрию/материалы НЕ освобождаем: они общие для
// шаблона гранаты (makeNade клонирует с общими geo/mat) — dispose сломал бы
// остальные гранаты того же типа. Шаблонов всего четыре, живут всё время.
function dropMesh(m) {
  scene.remove(m);
}

/**
 * Бросок гранаты, которую держим в руке (сила = накопленный замах).
 *
 * Онлайн: шлём намерение и СРАЗУ рисуем предсказанную гранату. Ждать полпинга
 * (≈125 мс при RTT 250), пока она вылетит из руки, невозможно — бросок
 * ощущался бы залипшим. Предсказанная граната только летит: взрывает и
 * наносит урон исключительно сервер. Когда приедет серверная копия
 * (opознаём по owner === me.id), предсказанную снимаем.
 */
export function throwCurrentNade() {
  const type = S.nadeMode; if (!type) return;
  const t = clock.elapsedTime;
  // Троттл зеркалит серверный THROW_COOLDOWN: иначе клиент рисовал бы
  // гранаты, которые сервер отбрасывает, и они бесследно исчезали бы.
  if (t - S.lastNade < 0.3) return; S.lastNade = t;
  const charge = S.nadeCharge; exitNade();

  const g = {
    mesh: nadeMesh(type), vel: throwVelocity(charge), type,
    // ⚠️ Зеркало FUSE_FAST/FUSE_SLOW из shared/src/grenade.rs — менять парой.
    fuse: (type === 'frag' || type === 'flash') ? 1.6 : 3.0,
    landed: false, acc: 0,
    // ↓ только для онлайна: сверка предсказания с сервером
    id: null,                       // id серверной копии, когда она опознается
    pos: throwOrigin(),             // симулируемая позиция (правда предсказания)
    corr: new THREE.Vector3(),      // визуальное смещение, гасится за ~0.4 с
    hist: [],                       // история {t,x,y,z} для сверки с сервером
    simT: performance.now(),        // время, которому соответствует pos
  };
  g.mesh.position.copy(g.pos);

  if (online()) {
    sendThrow(NADE_KIND[type], S.yaw, S.pitch, charge);
    predicted.push(g);            // взрыв и урон придут с сервера
  } else {
    grenades.push(g);             // офлайн-песочница: считаем всё сами
  }
  // Свой бросок — сразу в колонки, без затухания и без ожидания сервера
  // (тот же принцип, что у своего выстрела). Чужой бросок озвучивает
  // onNadeFrame, когда граната впервые появляется в кадре.
  if (!playFx('throw', 0.45)) tone(320, 0.05, 0.12, 'square');
}

// ============================================================
//  Серверные гранаты (онлайн)
// ============================================================

/** Свои гранаты, нарисованные до подтверждения сервером */
const predicted = [];
/** Гранаты с сервера: id → { mesh, prev, next } либо { own } для своей предсказанной */
const netNades = new Map();

// Пороги сверки своей гранаты с сервером — по образцу prediction.js.
const NADE_IGNORE = 0.2;   // ниже — шум квантования (1 см) и разница момента шага
const NADE_SNAP = 3.0;     // выше — реальный рассинхрон, сглаживать нечего
const NADE_CORR_DECAY = 6; // 1/с: визуальное смещение гаснет примерно за 0.4 с
const NADE_HIST_MS = 2000;

/** Где предсказанная граната была в момент `t` (мс, performance.now) */
function sampleHist(h, t) {
  if (!h.length) return null;
  if (t <= h[0].t) return h[0];
  for (let i = h.length - 1; i >= 0; i--) {
    if (h[i].t <= t) {
      const b = h[i + 1];
      if (!b) return h[i];
      const a = (t - h[i].t) / (b.t - h[i].t);
      return { x: h[i].x + (b.x - h[i].x) * a, y: h[i].y + (b.y - h[i].y) * a, z: h[i].z + (b.z - h[i].z) * a };
    }
  }
  return h[0];
}

/**
 * Сверка своей предсказанной гранаты с серверной позицией.
 *
 * ⚠️ Главное: серверный кадр описывает НЕ «сейчас», а момент примерно
 * `now - ping/2`. Сравнивать его с текущей предсказанной позицией нельзя —
 * именно это и давало «граната откатывается назад и летит дальше».
 * Поэтому ошибку меряем по истории, а гасим её так же, как рассинхрон
 * движения игрока: правду двигаем сразу, картинку — плавно.
 */
function reconcileNade(g, x, y, z) {
  const p = sampleHist(g.hist, performance.now() - (S.ping || 0) / 2);
  if (!p) return;
  const ex = x - p.x, ey = y - p.y, ez = z - p.z;
  const err = Math.hypot(ex, ey, ez);
  if (err < NADE_IGNORE) return;

  g.pos.set(g.pos.x + ex, g.pos.y + ey, g.pos.z + ez);
  // История тоже съезжает — иначе следующий кадр сообщит ту же ошибку ещё раз
  // и коррекция начнёт раскачиваться.
  for (const h of g.hist) { h.x += ex; h.y += ey; h.z += ez; }

  if (err > NADE_SNAP) g.corr.set(0, 0, 0);          // рывок честнее вранья
  else g.corr.set(g.corr.x - ex, g.corr.y - ey, g.corr.z - ez);
}

/**
 * Приём кадра OP_NADE.
 *
 * Позиции интерполируем с тем же `S.interpDelay`, что и чужих игроков.
 * Это не косметика: если граната поедет «в настоящем», а игроки — с
 * отставанием буфера, брошенная кем-то граната будет вылетать не из руки,
 * а из пустоты впереди него.
 */
function onNadeFrame(d, buf) {
  const count = d.getUint8(1);
  const now = performance.now();
  const alive = new Set();
  for (let i = 0; i < count; i++) {
    const o = 2 + i * 11;
    const id = d.getUint16(o, true);
    const owner = d.getUint16(o + 2, true);
    const type = NADE_NAME[d.getUint8(o + 4)];
    if (!type) continue;
    const x = unpackPos(d.getInt16(o + 5, true));
    const y = unpackPos(d.getInt16(o + 7, true));
    const z = unpackPos(d.getInt16(o + 9, true));
    alive.add(id);

    let n = netNades.get(id);
    if (!n) {
      // Наша граната доехала с сервера. Предсказанную НЕ подменяем: серверный
      // кадр описывает прошлое (доставка + буфер интерполяции), и подмена
      // отбрасывала гранату на несколько метров назад — «летит, дёргается,
      // летит дальше». Вместо этого привязываем id и дальше только сверяемся.
      if (owner === me.id) {
        const own = predicted.find(p => p.id === null);
        if (own) {
          own.id = id;
          netNades.set(id, { own });
          reconcileNade(own, x, y, z);
          continue;
        }
      }
      // Чужая граната появилась в кадре впервые — значит её только что
      // бросили, и это надо слышать: свист в шутере предупреждает раньше,
      // чем глаз найдёт саму гранату. Своя сюда не попадает — её перехватила
      // ветка выше, и звук броска уже сыгран локально.
      //
      // ⚠️ Утечки для читера здесь нет: летящая граната и так приходит ВСЕМ
      // без PVS-culling (room.rs считает её публичным объектом), то есть
      // звук не сообщает ничего сверх того, что уже есть в кадре.
      playFxAt('throw', x, y, z, 0.5, 16);
      n = { mesh: nadeMesh(type), prev: null, next: null };
      n.mesh.visible = false;   // пока интерполировать нечем — не рисуем (см. remote.js)
      netNades.set(id, n);
    }
    if (n.own) { reconcileNade(n.own, x, y, z); continue; }
    n.prev = n.next;
    n.next = { t: now, x, y, z };
  }
  // Гранаты, которых нет в свежем кадре, — взорвались или улетели из мира.
  // Сервер шлёт пустой кадр после последней, поэтому «повиснуть» они не могут.
  for (const [id, n] of netNades) {
    if (alive.has(id)) continue;
    if (n.own) {
      const i = predicted.indexOf(n.own);
      if (i >= 0) { dropMesh(n.own.mesh); predicted.splice(i, 1); }
    } else dropMesh(n.mesh);
    netNades.delete(id);
  }
  void buf;
}

function updateNetNades() {
  const target = performance.now() - S.interpDelay;
  for (const n of netNades.values()) {
    if (n.own) continue;                       // своя граната живёт предсказанием
    // Пока пары кадров нет — не рисуем вовсе. Показать «самую свежую» значит
    // сначала поставить гранату в актуальную точку, а с началом интерполяции
    // отпрыгнуть назад на interpDelay (тот же баг, что был в remote.js).
    if (!n.prev || !n.next) continue;
    n.mesh.visible = true;
    const span = n.next.t - n.prev.t;
    const a = span > 0 ? Math.max(0, Math.min(1, (target - n.prev.t) / span)) : 1;
    n.mesh.position.set(
      n.prev.x + (n.next.x - n.prev.x) * a,
      n.prev.y + (n.next.y - n.prev.y) * a,
      n.prev.z + (n.next.z - n.prev.z) * a,
    );
  }
}

/** Снять все серверные и предсказанные гранаты (обрыв связи) */
function clearNetNades() {
  // У своей гранаты в записи нет меша — он живёт в predicted и снимается ниже
  for (const n of netNades.values()) if (!n.own) dropMesh(n.mesh);
  netNades.clear();
  while (predicted.length) dropMesh(predicted.pop().mesh);
}

onMessage((op, d, buf) => {
  if (op === OP.NADE) { onNadeFrame(d, buf); return; }

  if (op === OP.BOOM) {
    // Взрыв целиком решён сервером — рисуем эффект в его точке.
    const type = NADE_NAME[d.getUint8(1)];
    if (!type) return;
    const pos = new THREE.Vector3(
      unpackPos(d.getInt16(2, true)),
      unpackPos(d.getInt16(4, true)),
      unpackPos(d.getInt16(6, true)),
    );
    // Поверхность удара (протокол 18) — только у молотова, у остальных
    // FACE_NONE. Вывести её самим нельзя: чужую бутылку клиент не симулирует,
    // а удар в стену и удар в пол выглядят в кадре одинаково — тремя числами.
    boomFx(type, pos, d.getUint8(8));
    return;
  }

  if (op === OP.FLASH) {
    // Силу ослепления считает сервер: он знает, кто куда смотрел и кого
    // закрывала стена. Клиент только рисует (формулы — как в прототипе).
    applyFlash(unpackUnit(d.getUint8(1)));
  }
});

// Палитра язычков: от белёсо-жёлтого у основания к красному кончику.
// ⚠️ Подняли по яркости (жалоба владельца «огонь тусклый»): первые два цвета
// почти белые, и именно они дают ощущение жара — аддитивное смешение делает
// из пятна такого цвета светящееся ядро, а не оранжевую кляксу.
const FLAME_COLORS = [0xfff3c4, 0xffd166, 0xffa62b, 0xff6a13];

/**
 * Одна частица горящей лужи молотова. Вызывается пачкой каждый кадр (плотность
 * задаёт updateGrenades пропорционально площади лужи). Три сорта частиц дают
 * объёмный огонь вместо прежней одинокой искры:
 *   • пламя — аддитивные язычки, всплывают ускоряясь (gravity<0) и сужаются к
 *     концу жизни (shrink), читаясь как тающий кверху язык;
 *   • угольки — мелкие ярко-жёлтые искры у самой земли;
 *   • дым — редкие тёмные клубы, обычная полупрозрачность (аддитивный серый
 *     светился бы как огонь), уходят выше пламени и раздуваются.
 */
function addFire(x, y, z) {
  const roll = Math.random();

  if (roll < 0.07) {                                  // тёмный дым над огнём
    // Дыма стало меньше (было 10 % частиц): он серый и НЕ аддитивный, то есть
    // гасит собой свечение — а огонь просили ярче. Заодно он поднят пониже,
    // чтобы не отрывался от прижатого к земле пламени.
    const m = new THREE.Mesh(new THREE.SphereGeometry(0.18, 6, 6),
      new THREE.MeshBasicMaterial({
        color: 0x181818, transparent: true, opacity: 0, depthWrite: false,
      }));
    m.position.set(x, y + 0.35 + Math.random() * 0.35, z);
    const life = 1.0 + Math.random() * 0.7;
    m.userData = {
      vel: new THREE.Vector3((Math.random() - 0.5) * 0.4, 0.8 + Math.random() * 0.5, (Math.random() - 0.5) * 0.4),
      life, life0: life, gravity: -0.4, drag: 0.8, grow: 1.5, op: 0.26,
    };
    scene.add(m); particles.push(m);
    return;
  }

  if (roll < 0.2) {                                   // уголёк у земли
    const m = new THREE.Mesh(new THREE.SphereGeometry(0.035, 4, 4),
      new THREE.MeshBasicMaterial({
        color: 0xffe27a, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false,
      }));
    m.position.set(x, y + 0.02, z);
    const life = 0.35 + Math.random() * 0.35;
    m.userData = {
      vel: new THREE.Vector3((Math.random() - 0.5) * 1.4, 1.0 + Math.random() * 1.1, (Math.random() - 0.5) * 1.4),
      life, life0: life, gravity: 3, drag: 1.4, shrink: true,
    };
    scene.add(m); particles.push(m);
    return;
  }

  // Основной язык пламени.
  //
  // ⚠️ Огонь СТЕЛЕТСЯ ПО ЗЕМЛЕ (правка по замечанию владельца «огонь нужен
  // ближе к земле и ярче»). Раньше язычки стартовали вверх со скоростью
  // 1.4…3.0 м/с и жили до 0.9 с — то есть уходили на два метра вверх, и лужа
  // читалась как столб дыма с искрами, а не как разлитый горящий бензин.
  // Теперь подъём вдвое медленнее, жизнь короче, зато язычки крупнее и их
  // втрое больше (плотность задаёт `updateGrenades`): пламя плотное, низкое
  // и живое. Одновременно живущих частиц при этом примерно столько же —
  // короткая жизнь компенсирует частое рождение (это важно для fill rate,
  // см. баг-урок про 60 → 20 FPS).
  const size = 0.15 + Math.random() * 0.13;
  const m = new THREE.Mesh(new THREE.SphereGeometry(size, 6, 5),
    new THREE.MeshBasicMaterial({
      color: FLAME_COLORS[Math.floor(Math.random() * FLAME_COLORS.length)],
      blending: THREE.AdditiveBlending, transparent: true, depthWrite: false,
    }));
  // Стартуют язычки У САМОЙ ЗЕМЛИ и растекаются вбок сильнее, чем вверх.
  m.position.set(x, y + 0.02, z);
  const life = 0.34 + Math.random() * 0.34;
  m.userData = {
    vel: new THREE.Vector3((Math.random() - 0.5) * 1.1, 0.65 + Math.random() * 0.85, (Math.random() - 0.5) * 1.1),
    life, life0: life, gravity: -0.8, drag: 1.3, shrink: true,
  };
  scene.add(m); particles.push(m);
}

/**
 * Язык пламени НА СТЕНЕ: та же частица, но всплывает вдоль вертикальной
 * плоскости и слегка отжимается от неё наружу по нормали.
 *
 * Зачем отдельно от `addFire`: у настенного огня другая геометрия — он не
 * растекается по кругу, а ползёт вверх узкой полосой и обязан прижиматься
 * к стене, иначе языки висят в воздухе в полуметре от неё.
 */
function addWallFire(x, y, z, nx, nz) {
  const size = 0.13 + Math.random() * 0.12;
  const m = new THREE.Mesh(new THREE.SphereGeometry(size, 6, 5),
    new THREE.MeshBasicMaterial({
      color: FLAME_COLORS[Math.floor(Math.random() * FLAME_COLORS.length)],
      blending: THREE.AdditiveBlending, transparent: true, depthWrite: false,
    }));
  m.position.set(x, y, z);
  const life = 0.3 + Math.random() * 0.3;
  m.userData = {
    // вдоль стены — вбок понемногу, от стены — почти никак (0.25 м/с наружу)
    vel: new THREE.Vector3(
      nz * (Math.random() - 0.5) * 0.6 + nx * 0.25,
      1.1 + Math.random() * 0.9,
      nx * (Math.random() - 0.5) * 0.6 + nz * 0.25,
    ),
    life, life0: life, gravity: -1.2, drag: 1.4, shrink: true,
  };
  scene.add(m); particles.push(m);
}

/**
 * Применить ослепление. Онлайн силу присылает сервер, офлайн считаем сами.
 * Формулы одни и те же, чтобы вспышка ощущалась одинаково в обоих режимах.
 */
function applyFlash(strength) {
  if (strength <= 0) return;
  // ⚠️ Эти две строки — зеркало shared/src/grenade.rs (FLASH_FULL = 0.55,
  // FLASH_HOLD_PER_STRENGTH = 5): по ним сервер считает, сколько времени
  // экран залит ЦЕЛИКОМ, и на это время вырезает всех игроков из снапшота
  // (античит «вижу при ослеплении»). Менять только вместе с сервером —
  // разъедутся, и либо белизна станет прозрачной для чита, либо честный
  // игрок увидит белый экран, за которым врагов уже нет в данных.
  S.flashAmt = Math.min(1, 0.45 + strength);        // близко → полностью белый (=1)
  S.flashHold = 5 * strength;                       // держит белый экран до 5 с
  S.flashDecay = 0.07 + (1 - strength) * 0.2;       // затем очень медленный возврат (~14 с)
  document.getElementById('flash').style.opacity = S.flashAmt;
  // Звон в ушах включается вместе с белизной и дальше ведётся ею же
  // (см. хвост updateGrenades). Здесь только резкий старт в момент вспышки.
  setTinnitus(S.flashAmt);
}

/**
 * Визуальная часть взрыва: звук, осколки, дым, огонь, тряска.
 *
 * Вызывается и по кадру OP_BOOM (онлайн), и локально (офлайн). Урона НЕ
 * наносит — в онлайне его целиком считает сервер, и попытка снять HP здесь
 * дала бы тот же баг, что уже описан в CLAUDE.md для hurtPlayer(): урон
 * списался бы и тут же был затёрт следующим OP_SELF.
 */
function boomFx(type, pos, face = FACE_NONE) {
  // Взрыв слышен почти отовсюду, но громкость и панорама зависят от того,
  // где он рванул: иначе граната на другом конце карты звучала так же,
  // как под ногами, и понять «куда прилетело» было нельзя.
  //
  // Каждый тип звучит по-своему — по звуку обязано читаться, ЧТО прилетело:
  // от осколочной надо убегать, от дымовой нет. Семпл не загрузился —
  // играет прежний синтез (`out` — тот же позиционный узел для обоих).
  const out = boomOut(pos.x, pos.y, pos.z);

  if (type === 'frag') {
    if (!playFxAt('frag', pos.x, pos.y, pos.z, 0.9, BOOM_REF)) {
      noise(0.3, 500, 0.6, 'lowpass', out); tone(60, 0.3, 0.4, 'sawtooth', out);
    }
    spawnBoom(pos, 16);           // ядро, огонь, осколки по всей зоне, ударная волна
    if (player.pos.distanceTo(pos) < 14) S.shake = Math.min(S.shake + 0.14, 0.18);

  } else if (type === 'smoke') {
    // Хлопок вышибного заряда + шипение газа на всё время выпуска. Дым виден
    // 30 с, а травит газ только первые секунды — шипение живёт SMOKE_HISS.
    if (!playFxAt('smokePop', pos.x, pos.y, pos.z, 0.5, 26)) noise(0.2, 300, 0.25, 'lowpass', out);
    startAmbient('hiss', pos.x, pos.y + 0.9, pos.z, { gain: 0.55, ref: 9, life: SMOKE_HISS, fadeIn: 0.15, fadeOut: 3.0 });
    spawnSmoke(pos);

  } else if (type === 'flash') {
    if (!playFxAt('flash', pos.x, pos.y, pos.z, 0.85, BOOM_REF)) {
      noise(0.15, 2500, 0.5, 'highpass', out);
    }
    // ⚠️ Звона здесь БОЛЬШЕ НЕТ, и это не потеря: раньше на хлопок вешался
    // короткий позиционный `tone(1800, 0.5, …)`, то есть звенело у всех, кто
    // взрыв слышал, полсекунды и одинаково — хоть ослепило, хоть нет.
    // Теперь звон ведёт само ослепление (`applyFlash` → `setTinnitus`): он
    // стоит в ушах ровно столько, сколько белый экран, и гаснет вместе с ним.
    spawnSparks(pos, new THREE.Vector3(0, 1, 0), 0xffffff, 28);

  } else if (type === 'molotov') {
    // Сначала бьётся бутылка, потом горит лужа — ровно то, что видно глазами.
    if (!playFxAt('glass', pos.x, pos.y, pos.z, 0.7, 20)) {
      noise(0.25, 400, 0.4, 'lowpass', out); tone(120, 0.2, 0.3, 'sawtooth', out);
    }
    // Брызги стекла и горючего — В ТОЧКЕ УДАРА, по нормали поверхности.
    // Именно они читаются как «бутылка разбилась ЗДЕСЬ», когда удар пришёлся
    // в стену на высоте, а лужа при этом внизу.
    const n = FACE_NORMAL[face] || [0, 1, 0];
    spawnSparks(pos, new THREE.Vector3(n[0], n[1], n[2]), 0xffa62b, 14);

    // ⚠️ Лужа ложится на ПОЛ ПОД точкой удара — зеркало `room.rs::detonate`
    // (`grenade::floor_under`). Разбилась о стену на высоте 3 м — горит у её
    // подножия, разбилась о человека — горит у него под ногами. Держать её
    // на высоте удара значит не жечь никого: `FIRE_HEIGHT` = 2 м.
    // ⚠️ И растекается лужа ПО ПОЛУ, а не кругом: `spill` заливает клетки
    // от точки удара, не проходя сквозь стены и не перепрыгивая обрывы
    // (жалоба владельца «поджигает за стеной и висит в воздухе»).
    const cells = spill(pos);
    const base = new THREE.Vector3(cells[0].x, cells[0].y, cells[0].z);
    startAmbient('fire', base.x, base.y + 0.3, base.z,
      { gain: 0.6, ref: 8, life: FIRE_LIFE, fadeIn: 0.5, fadeOut: 2.5 });
    // Онлайн лужа только горит для глаз: тик-урон считает сервер.
    fires.push({
      pos: base, cells, radius: FIRE_RADIUS, born: clock.elapsedTime,
      until: clock.elapsedTime + FIRE_LIFE, visual: online(),
      // Удар пришёлся в стену — огонь разливается ещё и ПО НЕЙ, от точки
      // удара вниз до пола. Хранится точка удара и нормаль стены.
      wall: isWallFace(face)
        ? { x: pos.x, y: pos.y, z: pos.z, nx: n[0], nz: n[2] }
        : null,
    });
  }
}

/** Локальный взрыв со всей его логикой — только офлайн (боты, песочница) */
function detonate(g) {
  const pos = g.mesh.position.clone();
  // Грань удара офлайн знает сама физика (`stepNade` положила её в `g.face`),
  // онлайн она приезжает байтом в кадре OP_BOOM.
  boomFx(g.type, pos, g.face ?? FACE_NONE);

  if (g.type === 'frag') {
    // осколочная: радиус 16, спад от дистанции; стены закрывают от осколков
    const RAD = 16, src = pos.clone().setY(pos.y + 0.3);
    // луч от взрыва к цели — если между ними стена, урона нет (человек за стеной защищён)
    const clear = (to) => {
      const dir = to.clone().sub(src), dist = dir.length();
      if (dist < 0.001) return true;
      losRay.set(src, dir.normalize()); losRay.far = dist;
      return !losRay.intersectObjects(wallMeshes, false).some(h => h.distance < dist - 0.4);
    };
    // Урон по ботам здесь больше не считается: боты уехали на сервер, и
    // осколки им наносит `room.rs::detonate` тем же кодом, что игрокам.
    // Раньше NPC жили в браузере, сервер о них не знал — потому граната
    // по ним и не работала (жалоба владельца).
    if (player.alive) {
      const d = player.pos.distanceTo(pos);
      if (d < RAD && clear(player.pos.clone())) hurtPlayer(Math.round(110 * (1 - d / RAD)));
    }

  } else if (g.type === 'flash') {
    // офлайн считаем силу вспышки сами — зеркало grenade.rs::flash_strength
    camera.getWorldPosition(_wp); camera.getWorldDirection(_wd);
    const toN = pos.clone().sub(_wp), dist = toN.length(), RANGE = 30;
    if (dist < RANGE) {
      toN.normalize(); const facing = Math.max(0, _wd.dot(toN));
      losRay.set(_wp, toN); losRay.far = dist;
      const blocked = losRay.intersectObjects(wallMeshes, false).some(h => h.distance < dist - 0.5);
      if (!blocked) applyFlash((1 - dist / RANGE) * (0.35 + 0.65 * facing));
    }
  }
}

// ---- Дым (эталон — дымовая CS2, замер владельца) ----
/** Радиус облака по земле, м. Вдвое меньше прежних 12.8 — замер владельца
 *  «дым слишком большой». ⚠️ Вместе с ним обязаны уменьшаться и слои клубов
 *  (`LAYERS` в spawnSmoke): радиус без размера спрайтов даёт рыхлое облако
 *  с просветами, размер без радиуса — сплошную лепёшку. */
const SMOKE_RAD = 6.4;
/** Высота столба над точкой взрыва, м (те же −50 % от прежних 5.2). */
const SMOKE_TOP = 2.6;
/** За сколько секунд облако раздувается из точки взрыва до полного размера.
 *  ⚠️ Это ЕДИНСТВЕННОЕ разрастание дыма, какое допустимо: дальше облако
 *  обязано стоять в первом охвате и только выцветать (требование владельца
 *  2026-07-29). Что именно росло сверх этого — в хвосте `updateGrenades`. */
const SMOKE_FILL = 0.55;
/** Насколько клубы всплывают за всю жизнь облака, м. Потолок, а не скорость:
 *  подъём даёт дыму жизнь, но не должен выносить его за первый охват. */
const SMOKE_RISE_MAX = 0.5;
/** Цвет клубов. Тёмно-серый, а не белёсый: светлый дым читался как туман. */
const SMOKE_COLOR = 0x8d9198;
/** Сколько живёт облако, с. */
const SMOKE_LIFE = 30;
/** Оверлей «камера внутри дыма» и последнее выставленное ему значение. */
let smokeVeilEl = null, smokeVeilShown = 0;
/** То же для оранжевой каймы «я горю» (см. хвост updateGrenades). */
let burnVeilEl = null, burnVeilShown = 0;

/** Облако дыма (только графика — на сервере дыма нет по решению, см. CLAUDE.md) */
function spawnSmoke(pos) {
  // Дым: плотная непрозрачная тумба у земли, встаёт почти сразу (SMOKE_FILL)
  // и рассеивается к концу SMOKE_LIFE.
  //
  // ⚠️ Баг-урок: здесь стоял `noise(0.2, 300, 0.25)` БЕЗ узла назначения, то
  // есть хлопок дымовой всегда играл прямо в колонки на полной громкости —
  // хоть под ногами, хоть на другом конце карты. Тот же класс ошибки, что
  // разобран в md/sound.md про `boomOut` и `null`: у `noise/tone` отсутствие
  // узла означает «в динамики», а не «тихо». Теперь звук выпуска играет
  // boomFx позиционно, до вызова spawnSmoke.
  const puffs = [];
  // Три слоя вместо одной «лепёшки». Нижний — самый плотный и самый крупный:
  // жалоба владельца была именно про просветы у земли, сквозь которые ноги
  // видно, хотя дым стоит. Верхний уже нижнего — получается тумба со
  // скруглённой шапкой, а не столб.
  //
  // ⚠️ Все высоты и размеры спрайтов уменьшены вдвое вместе с SMOKE_RAD/SMOKE_TOP.
  // Число клубов при этом НЕ трогаем: площадь облака упала вчетверо, но и площадь
  // спрайта тоже — плотность сохраняется ровно тем, что счётчики остались прежними.
  const LAYERS = [
    { n: 34, y0: 0.18, dy: 0.4, r: 1.00, sc: 3.3, dsc: 1.3 },  // подошва: перекрывает землю
    { n: 22, y0: 0.75, dy: 0.8, r: 0.92, sc: 3.0, dsc: 1.2 },  // тело
    { n: 14, y0: 1.5,  dy: 1.1, r: 0.72, sc: 2.6, dsc: 1.0 },  // шапка
  ];
  for (const L of LAYERS) {
    for (let i = 0; i < L.n; i++) {
      const s = new THREE.Sprite(new THREE.SpriteMaterial({
        map: PUFF, color: SMOKE_COLOR, transparent: true, opacity: 0, depthWrite: false,
      }));
      const ang = Math.random() * 6.283;
      // ⚠️ sqrt(random), а не random: равномерно по ПЛОЩАДИ круга. С линейным
      // радиусом клубы сгущались к центру, и по краям облака оставались дыры —
      // ровно тот же приём, что уже стоит у лужи молотова ниже.
      const rad = Math.sqrt(Math.random()) * SMOKE_RAD * L.r;
      // ⚠️ Высота клубов — от точки взрыва, а не от нуля: дымовая, сработавшая
      // на террасе, иначе висела бы облаком во дворе семью метрами ниже.
      // Позицию держим смещением от центра: в первые SMOKE_FILL секунд облако
      // раздувается наружу (см. updateGrenades), а вертикальный снос копится
      // отдельно в `rise` — иначе одно затирало бы другое.
      s.userData = {
        dx: Math.cos(ang) * rad, dy: L.y0 + Math.random() * L.dy, dz: Math.sin(ang) * rad,
        rise: 0,
        op: 0.85 + Math.random() * 0.15,
        born: Math.random() * 0.3,        // лёгкий разнобой, но не задержка появления
      };
      s.position.set(pos.x, pos.y + s.userData.dy * 0.2, pos.z);
      const sc = L.sc + Math.random() * L.dsc; s.scale.set(sc, sc, 1);
      scene.add(s); puffs.push(s);
    }
  }
  // Геометрия для чужой видимости (remote.js::occluded) — ЦИЛИНДР, а не шар:
  // облако низкое и широкое, и шаром радиуса 12.8 оно прятало бы стоящего
  // десятью метрами выше на террасе, где дыма нет вовсе.
  smokes.push({
    born: clock.elapsedTime, life: SMOKE_LIFE, puffs,
    cx: pos.x, cy: pos.y + SMOKE_TOP * 0.45, cz: pos.z,
    by: pos.y,                                  // база: с неё отмеряются слои клубов
    y0: pos.y - 0.5, top: pos.y + SMOKE_TOP,    // границы цилиндра облака
    rad: SMOKE_RAD, hidden: false,
  });
}

export function updateGrenades(dt) {
  const t = clock.elapsedTime;

  // Офлайн-гранаты: летят и взрываются сами (песочница с ботами).
  // Шаг фиксированный (NADE_DT), чтобы траектория совпадала с превью и сервером.
  for (let i = grenades.length - 1; i >= 0; i--) {
    const g = grenades[i];
    const fragile = g.type === 'molotov';
    g.acc = (g.acc || 0) + dt;
    while (g.acc >= NADE_DT) {
      g.acc -= NADE_DT;
      if (g.landed && fragile) break;      // разбитая бутылка не летит
      if (stepNade(g.mesh.position, g.vel, NADE_DT, fragile)) g.landed = true;
      if (fragile && lastFace >= 0) g.face = lastFace;
      g.fuse -= NADE_DT;
    }
    // ⚠️ У молотова таймера НЕТ: он вспыхивает от касания (зеркало
    // `grenade.rs::should_detonate`). Условие по фитилю ему оставлено
    // страховкой от вечного объекта, ровно как на сервере.
    if ((fragile && g.landed) || g.fuse <= 0) {
      detonate(g); dropMesh(g.mesh); grenades.splice(i, 1);
    }
  }

  // Свои гранаты до подтверждения сервером: только летят, взрыв придёт кадром
  // OP_BOOM. Держим их не дольше фитиля с запасом — если серверная копия по
  // какой-то причине не доехала, предсказанная не должна остаться навсегда.
  for (let i = predicted.length - 1; i >= 0; i--) {
    const g = predicted[i];
    g.acc += dt;
    // Своя бутылка тоже не отскакивает: разбилась — стоит и ждёт, пока сервер
    // подтвердит взрыв кадром OP_BOOM и снимет её из кадра OP_NADE.
    const fragile = g.type === 'molotov';
    while (g.acc >= NADE_DT) {
      g.acc -= NADE_DT;
      // Разбилась — стоит на месте. Без этого гравитация продолжала бы её
      // тащить: у стены бутылка сползала бы по ней вниз всё то время
      // (полпинга), пока не приедет серверный OP_BOOM.
      if (g.landed) break;
      if (stepNade(g.pos, g.vel, NADE_DT, fragile) && fragile) g.landed = true;
      g.fuse -= NADE_DT;
      // Время шага, а не кадра: серверную позицию сверяем именно с ним.
      g.simT += NADE_DT * 1000;
      g.hist.push({ t: g.simT, x: g.pos.x, y: g.pos.y, z: g.pos.z });
    }
    while (g.hist.length > 1 && g.simT - g.hist[0].t > NADE_HIST_MS) g.hist.shift();

    // Расхождение с сервером гасим плавно: рывок читается как лаг, даже когда он верный
    const k = Math.max(0, 1 - NADE_CORR_DECAY * dt);
    g.corr.multiplyScalar(k);
    g.mesh.position.copy(g.pos).add(g.corr);

    // Подтверждённую сервером гранату снимает кадр OP_NADE (она пропала → взорвалась).
    // Неподтверждённую держим не дольше фитиля с запасом: если серверная копия
    // не доехала, предсказанная не должна остаться в воздухе навсегда.
    if (g.id === null && g.fuse <= -1) { dropMesh(g.mesh); predicted.splice(i, 1); }
  }

  // Оборвалась связь — серверные гранаты обновлять больше некому, иначе
  // последняя так и зависла бы в воздухе до перезагрузки страницы.
  if (!online() && (netNades.size || predicted.length)) clearNetNades();
  else updateNetNades();

  if (smokes.length) camera.getWorldPosition(_wp);
  let veil = 0;                       // самая плотная пелена из всех облаков
  for (let i = smokes.length - 1; i >= 0; i--) {
    const s = smokes[i], age = t - s.born;
    // Плотность облака целиком: те же полсекунды на выпуск, что у клубов, и
    // общее рассеивание в конце. По ней же считается пелена — иначе, войдя
    // в ещё не сгустившийся дым, игрок мгновенно слепнет.
    const dens = Math.min(1, Math.max(0, age / SMOKE_FILL))
               * Math.min(1, Math.max(0, (s.life - age) / 6));

    // Насколько глубоко камера внутри облака (0 — снаружи, 1 — заведомо внутри).
    const hd = Math.hypot(_wp.x - s.cx, _wp.z - s.cz);
    // Полная непрозрачность набирается за 4 м от кромки: шаг внутрь ослепляет,
    // но у самого края ещё видно, что ты входишь в дым, а не в стену.
    const inside = Math.min(1, Math.max(0, (s.rad - hd) / 4))
      * Math.min(1, Math.max(0, (s.top - _wp.y) / 1.2))
      * Math.min(1, Math.max(0, (_wp.y - s.y0) / 0.8));
    const deep = inside * dens;
    if (deep > veil) veil = deep;

    // Камера внутри плотного ядра — сами клубы не видны за пеленой, и рисовать
    // их незачем. ⚠️ Это не косметика, а бюджет GPU: полтора десятка огромных
    // прозрачных спрайтов вплотную к камере — худший случай по fill rate,
    // ровно тот, из-за которого уже был провал 60 → 20 FPS (md/bug-lessons.md).
    const hide = deep > 0.93;
    if (hide !== s.hidden) { s.hidden = hide; for (const p of s.puffs) p.visible = !hide; }

    if (!hide) {
      for (const p of s.puffs) {
        const u = p.userData;
        // Медленный подъём — но С ПОТОЛКОМ. Без него за SMOKE_LIFE = 30 с клубы
        // уезжали на 3 м, то есть выше всего столба (SMOKE_TOP = 2.6): облако
        // отрывалось от земли и вылезало за свой цилиндр видимости. Полметра
        // хватает, чтобы дым «жил», и не выводит его за первый охват.
        u.rise = Math.min(SMOKE_RISE_MAX, u.rise + dt * 0.1);
        const a = age - u.born;                                   // своё время у каждого клуба
        // Раздувание: клубы вылетают из точки взрыва и за SMOKE_FILL встают на
        // свои места. Замедление к концу (ease-out) — дым выхлопывает разом,
        // а не разъезжается равномерно, как надувной шар.
        const k = Math.min(1, Math.max(0, a / SMOKE_FILL));
        const e = 0.2 + 0.8 * k * (2 - k);
        p.position.set(s.cx + u.dx * e, s.by + u.dy * e + u.rise, s.cz + u.dz * e);

        const fadeIn = Math.min(1, Math.max(0, a / 0.35));          // появление почти мгновенное
        const fadeOut = Math.min(1, Math.max(0, (s.life - age) / 6)); // плавное рассеивание ~6 с
        const near = Math.min(1, p.position.distanceTo(_wp) / 2.5); // клуб у камеры → прозрачный (нет «квадрата»)
        p.material.opacity = u.op * fadeIn * fadeOut * near;
        // ⚠️ Спрайты БОЛЬШЕ НЕ РАСТУТ. Здесь стояло `p.scale *= 1 + dt*0.025`
        // «очень лёгкое расширение» — но это множитель за СЕКУНДУ, и за
        // SMOKE_LIFE = 30 с он даёт e^0.75 ≈ 2.1, то есть облако к концу жизни
        // раздувалось вдвое против первого охвата. Жалоба владельца
        // «дым разрастается» — ровно про это. Заодно расхождение с геймплеем:
        // цилиндр видимости (`rad`/`top` в `smokes`) не растёт вовсе, поэтому
        // раздувшаяся картинка накрывала места, где сервер дыма не видит.
      }
    }
    if (age > s.life) {
      s.puffs.forEach(p => { scene.remove(p); p.material.dispose(); }); smokes.splice(i, 1);
    }
  }
  // Пелена «внутри дыма»: спрайты у самой камеры принудительно прозрачны
  // (`near`), поэтому изнутри облако само по себе просвечивает. Заливка
  // добивает картинку до честного «не видно ничего» — 0.985, а не 1, чтобы
  // остался намёк на свет и не выглядело выключенным монитором.
  // В DOM пишем только при изменении: стиль трогается каждый кадр, а смена
  // opacity на элементе во весь экран заставляет браузер перерисовывать слой.
  const va = Math.round(veil * 0.985 * 100) / 100;
  if (va !== smokeVeilShown) {
    smokeVeilShown = va;
    if (!smokeVeilEl) smokeVeilEl = document.getElementById('smokeveil');
    smokeVeilEl.style.opacity = va;
  }

  // Стоит ли игрок в огне прямо сейчас — нужно офлайновому горению внизу.
  let inFire = false;
  for (let i = fires.length - 1; i >= 0; i--) {
    const f = fires[i];
    // Плотность язычков ∝ площади лужи и не зависит от FPS (масштаб через dt).
    // Огонь плавно разгорается за ~0.4 с и угасает за последние ~2 с, иначе
    // возникал и гас бы резким пятном. Точки берём с sqrt(random), чтобы
    // покрытие было равномерным по кругу, а не сгущалось в центре.
    //
    // ⚠️ ЭТО ГЛАВНАЯ РУЧКА «СКОЛЬКО СТОИТ ОГОНЬ». Каждый язычок — отдельный
    // меш, то есть отдельный draw call, и живут они одновременно сотнями.
    // Считать надо не «частиц в секунду», а СКОЛЬКО ЖИВЁТ ОДНОВРЕМЕННО:
    // rate × средняя жизнь × площадь. Было: 3.5 × 0.68 с × π·6² ≈ 270 язычков.
    // Стало: 3.0 × 0.51 с × π·9² ≈ 390 — в полтора раза больше при вдвое
    // большей луже, и это осознанный потолок. Крутить вверх только с замером
    // FPS на слабой видеокарте: провал 60 → 20 из-за такой же мелочи уже был
    // (md/bug-lessons.md). Ощущение «огня стало больше» даёт не только число:
    // язычки крупнее, живут короче (значит, чаще шевелятся) и стелются низко.
    const grow = f.born ? Math.min(1, (clock.elapsedTime - f.born) / 0.4) : 1;
    const fade = Math.min(1, Math.max(0, (f.until - t) / 2));
    // ⚠️ Язычки рождаются В КЛЕТКАХ разлива, а не по кругу вокруг центра.
    // Это и есть лечение «огонь висит в воздухе»: у клетки своя высота пола,
    // и над обрывом (или по ту сторону стены) клеток просто нет. Плотность
    // на единицу площади прежняя — `FIRE_CELL²` на клетку вместо πR² на круг,
    // то есть потолок частиц не вырос (см. предупреждение выше про fill rate).
    const cells = f.cells;
    const n = Math.round(cells.length * FIRE_CELL * FIRE_CELL * 3.0 * grow * fade * dt);
    for (let k = 0; k < n; k++) {
      const c = cells[(Math.random() * cells.length) | 0];
      addFire(c.x + (Math.random() - 0.5) * FIRE_CELL, c.y, c.z + (Math.random() - 0.5) * FIRE_CELL);
    }
    // Огонь на стене: полоса от точки удара вниз до пола. Разбилась бутылка
    // о стену — горит и стена, и пол под ней (требование владельца).
    if (f.wall) {
      const h = Math.max(0.4, f.wall.y - f.pos.y);          // высота полосы
      const wn = Math.round(h * 9 * grow * fade * dt);
      for (let k = 0; k < wn; k++) {
        // Ниже — гуще: горючее стекает вниз, а не висит у точки удара.
        const up = Math.pow(Math.random(), 1.6) * h;
        const side = (Math.random() - 0.5) * 2.2;
        addWallFire(
          f.wall.x + f.wall.nz * side + f.wall.nx * 0.12,
          f.pos.y + up,
          f.wall.z + f.wall.nx * side + f.wall.nz * 0.12,
          f.wall.nx, f.wall.nz,
        );
      }
    }
    // ⚠️ Зеркало `FirePatch::covers`: жжёт та клетка пола, на которой человек
    // стоит, а не весь круг радиусом девять метров вокруг центра. Ограничение
    // по высоте (`FIRE_HEIGHT`) осталось внутри — лужа не жжёт сквозь
    // перекрытие того, кто стоит этажом выше или ниже.
    const feet = player.pos.y - player.eye;
    if (player.alive && fireCovers(f.cells, player.pos.x, feet, player.pos.z)) inFire = true;
    if (t > f.until) fires.splice(i, 1);
  }

  // ---- Горение самого игрока ----
  //
  // Онлайн правду говорит сервер: он ставит бит F_BURNING в кадре OP_SELF
  // (`prediction.js`) и шлёт урон кадрами OP_DAMAGE. Снимать HP ещё и здесь —
  // тот же «двойной источник правды», что уже описан про hurtPlayer.
  // Офлайн (песочница) считаем сами — зеркало `room.rs::step_fires`:
  // попал в огонь → горишь BURN_AFTER секунд, вышел → догораешь.
  if (!online()) {
    if (inFire && player.alive) {
      if (S.burnLeft <= 0) S.burnNext = 0;
      S.burnLeft = BURN_AFTER;
    }
    if (S.burnLeft > 0) {
      if (!player.alive) S.burnLeft = 0;
      else {
        S.burnLeft -= dt;
        S.burnNext -= dt;
        // ⚠️ Второй аргумент `false` — огонь шаг НЕ сбивает (решение владельца:
        // «урон от огня — не то же самое, что урон от выстрелов»). Иначе тик
        // раз в 0.4 с продлевал бы окно HURT_SLOW непрерывно, и горящий полз бы
        // всё догорание. Зеркало `room.rs::step_fires`, который окна не ставит.
        if (S.burnNext <= 0) { S.burnNext = FIRE_TICK; hurtPlayer(FIRE_SELF_DMG, false); }
      }
    }
    S.burning = S.burnLeft > 0;
  }
  // Кайма «я горю». Пульсирует: ровная заливка читается как баг рендера,
  // а дышащая — как огонь. Гасим плавно, чтобы выход из лужи не мигал.
  const veilTarget = S.burning && player.alive ? 0.55 + 0.14 * Math.sin(t * 9) : 0;
  S.burnVeil += (veilTarget - S.burnVeil) * Math.min(1, dt * 8);
  const bv = Math.round(S.burnVeil * 100) / 100;
  if (bv !== burnVeilShown) {
    burnVeilShown = bv;
    if (!burnVeilEl) burnVeilEl = document.getElementById('burnveil');
    burnVeilEl.style.opacity = bv;
  }

  const flashEl = document.getElementById('flash');
  if (S.flashHold > 0) { S.flashHold -= dt; flashEl.style.opacity = S.flashAmt; }  // фаза полного белого
  else if (S.flashAmt > 0) {                                                        // плавный возврат
    S.flashAmt = Math.max(0, S.flashAmt - dt * S.flashDecay); flashEl.style.opacity = S.flashAmt;
  }
  // Звон в ушах гаснет ровно тем же числом, что и белизна: ослепление —
  // одно состояние, у которого просто две стороны, зрительная и слуховая.
  // ⚠️ Зовём безусловно, в том числе при flashAmt = 0: это же и единственная
  // точка, где звон глохнет после респавна (player.js обнуляет flashAmt
  // напрямую), а updateGrenades крутится и у мёртвого — см. main.js.
  setTinnitus(S.flashAmt);
}
